import React from 'react';
import { useTranslation } from 'localization';
import StatCardRowMolecule from '../../StatCardRow/StatCardRowMolecule';
import {
  StatCardAtomProps,
  StatCardTrendDirection,
} from '../../../atoms/StatCard/StatCardAtom';
import { UnifiedTasksCounts } from '../useTaskData';

export interface TaskListSummaryMoleculeProps {
  /**
   * Counts returned by `useTaskData` (or the unified tasks endpoint).
   * When `null`, the molecule renders nothing — consumers can show a skeleton
   * or placeholder while the first fetch resolves.
   */
  counts: UnifiedTasksCounts | null;
  /** Accessible label override applied to the underlying row. */
  ariaLabel?: string;
  className?: string;
}

interface CompletedTrend {
  direction: StatCardTrendDirection;
  pct: number;
  sign: '+' | '-' | '';
}

/**
 * Derives the 30d trend against the prior 30d window. Returns `null` when
 * either input is not a number or when `previous <= 0` (avoids divide-by-zero
 * and negative-denominator artifacts). Callers should render a fallback
 * caption in the `null` case.
 */
export function deriveCompleted30dTrend(
  current: number | undefined,
  previous: number | undefined
): CompletedTrend | null {
  if (typeof current !== 'number' || typeof previous !== 'number') {
    return null;
  }
  if (previous <= 0) {
    return null;
  }
  const pctRaw = ((current - previous) / previous) * 100;
  const pct = Math.round(pctRaw);
  if (pct > 0) return { direction: 'up', pct, sign: '+' };
  if (pct < 0) return { direction: 'down', pct, sign: '-' };
  return { direction: 'flat', pct: 0, sign: '' };
}

const TaskListSummaryMolecule: React.FC<TaskListSummaryMoleculeProps> = ({
  counts,
  ariaLabel,
  className = '',
}) => {
  const { t } = useTranslation();

  if (!counts) {
    return null;
  }

  const behindValue = counts.behind ?? counts.overdue;
  const hasUpcoming = typeof counts.upcoming === 'number';
  const hasCompleted30d = typeof counts.completed30d === 'number';

  const toDoStat: StatCardAtomProps = {
    label: t('taskList.summary.toDo'),
    value: counts.total,
    caption: t('taskList.summary.toDoCaption', {
      checkins: counts.checkIn,
      workflows: counts.workflow,
    }),
    accent: 'info',
  };

  const behindStat: StatCardAtomProps = {
    label: t('taskList.summary.behind'),
    value: behindValue,
    caption: t('taskList.summary.behindCaption', { total: behindValue }),
    tone: behindValue > 0 ? 'danger' : 'default',
    accent: behindValue > 0 ? 'danger' : undefined,
  };

  const upcomingStat: StatCardAtomProps = {
    label: t('taskList.summary.upcoming'),
    value: hasUpcoming ? (counts.upcoming as number) : '—',
    caption: t('taskList.summary.upcomingCaption'),
    tone: hasUpcoming && (counts.upcoming as number) > 0 ? 'success' : 'default',
  };

  const trend = deriveCompleted30dTrend(
    counts.completed30d,
    counts.completed30dPrev
  );
  const completedStat: StatCardAtomProps = {
    label: t('taskList.summary.completed30d'),
    value: hasCompleted30d ? (counts.completed30d as number) : '—',
  };
  if (trend) {
    completedStat.trend = {
      direction: trend.direction,
      label: t('taskList.summary.completed30dTrend', {
        sign: trend.sign,
        pct: Math.abs(trend.pct),
      }),
    };
  } else {
    completedStat.caption = t('taskList.summary.completed30dNoPrevious');
  }

  const stats: StatCardAtomProps[] = [
    toDoStat,
    behindStat,
    upcomingStat,
    completedStat,
  ];

  const rowClassName = ['task-list-summary-molecule', className]
    .filter(Boolean)
    .join(' ');

  return (
    <StatCardRowMolecule
      stats={stats}
      responsive
      ariaLabel={ariaLabel}
      className={rowClassName}
    />
  );
};

TaskListSummaryMolecule.displayName = 'TaskListSummaryMolecule';

export default TaskListSummaryMolecule;
